import React from 'react';
import { cva } from 'class-variance-authority';

import type { QuestRecurrence } from '@zealy/utils';
import {
  CheckboxRecLine,
  CountFilled,
  DiscordBrand,
  DollarCircleFilled,
  DollarLine,
  Edit3Filled,
  FastForwardLine,
  FolderLine,
  GiftLine,
  Image2Filled,
  LightningFilled,
  LoaderLine,
  RefreshCwLine,
  SparkleFilled,
  StarFilled,
  Timer2Line,
  TimerLine,
} from '@zealy/icons';

const STATUS = {
  label: 'status',
  value: 'published',
  icon: <LoaderLine />,
  options: [true, false] as const,
} as const;

export const RECURRENCE = {
  label: 'recurrence',
  value: 'recurrence',
  icon: <RefreshCwLine />,
  options: ['once', 'daily', 'weekly', 'monthly'] as QuestRecurrence[],
  showDescription: true,
  keepRawValue: false,
} as const;

export const COOLDOWN = {
  label: 'cooldown',
  value: 'retryAfter',
  icon: <TimerLine />,
  options: ['0', '60', '300', '1800', '3600', '86400', '604800', '2592000', '-1'] as const,
  showDescription: true,
  keepRawValue: false,
} as const;

export const CLAIM_LIMIT = {
  label: 'claim-limit',
  value: 'claimLimit',
  type: 'number',
  icon: <CountFilled />,
  showDescription: true,
  defaultValue: 0,
} as const;

export const CONDITIONS = {
  label: 'conditions',
  value: 'conditions',
  icon: <CheckboxRecLine />,
  options: ['level', 'role', 'nft', 'quest', 'date', 'twitterFollowers', 'twitterAccountAge'],
  showDescription: false,
} as const;

export const rewardIconStyles = cva('flex-shrink-0', {
  variants: {
    variant: {
      xp: 'icon-rewards-xp',
      nft: 'icon-rewards-nft',
      role: 'icon-rewards-role',
      other: 'icon-rewards-other',
      error: 'icon-onwarning-secondary',
      token: 'icon-rewards-token',
      zaps: 'icon-rewards-zaps',
      usdc: 'text-[#2775CA]', // USDC brand blue color for the circle
    },
  },
});

export const RewardIcons = {
  xp: StarFilled,
  role: DiscordBrand,
  nft: Image2Filled,
  other: Edit3Filled,
  token: DollarLine,
  zaps: LightningFilled,
  usdc: DollarCircleFilled,
};

export const REWARDS = {
  label: 'rewards',
  value: 'rewards',
  icon: <GiftLine />,
  options: ['xp', 'role', 'zaps', 'usdc', 'other', 'token'] as const,
  showDescription: false,
} as const;

export const MODULE = {
  label: 'module',
  value: 'categoryId',
  icon: <FolderLine />,
  showDescription: false,
} as const;

const SPRINT = {
  label: 'sprint',
  value: 'sprintId',
  icon: <Timer2Line />,
  showDescription: false,
} as const;

const AUTOMATIC_WHEN_EMPTY = {
  label: 'isAutomaticWhenEmpty',
  value: 'isAutomaticWhenEmpty',
  icon: <FastForwardLine />,
  options: [true, false] as const,
  showDescription: true,
  keepRawValue: true,
} as const;

const AI_REVIEW = {
  label: 'aiReview',
  value: 'aiReview',
  icon: <SparkleFilled />,
  options: [true, false] as const,
  showDescription: true,
  keepRawValue: true,
} as const;

export type SelectProperty = typeof RECURRENCE | typeof COOLDOWN | typeof AUTOMATIC_WHEN_EMPTY;

export type ClaimLimitProperty = typeof CLAIM_LIMIT;

export type ConditionsProperty = typeof CONDITIONS;

export type RewardsProperty = typeof REWARDS;

export type ModuleProperty = typeof MODULE;

export type SprintProperty = typeof SPRINT;

export type AutomaticWhenEmptyProperty = typeof AUTOMATIC_WHEN_EMPTY;

export type Property =
  | SelectProperty
  | ClaimLimitProperty
  | ConditionsProperty
  | RewardsProperty
  | ModuleProperty
  | SprintProperty
  | AutomaticWhenEmptyProperty
  | typeof AI_REVIEW;

export const PROPERTIES = [
  RECURRENCE,
  COOLDOWN,
  CLAIM_LIMIT,
  CONDITIONS,
  REWARDS,
  MODULE,
  SPRINT,
  AUTOMATIC_WHEN_EMPTY,
  AI_REVIEW,
] as const;

export const INLINE_PROPERTIES = [STATUS, RECURRENCE, COOLDOWN] as const;
